import clsx from 'clsx';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';

import styles from './CursorFollower.module.scss';

interface CursorFollowerProps {
  children: ReactNode;
}

export const CursorFollower: FC<CursorFollowerProps> = ({ children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const followerRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const isVisibleRef = useRef(false);
  const targetPos = useRef({ x: 0, y: 0 });
  const followerPos = useRef({ x: 0, y: 0 });
  const trailPos = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | null>(null);

  const syncElements = () => {
    if (followerRef.current) {
      followerRef.current.style.transform = `translate3d(${followerPos.current.x}px, ${followerPos.current.y}px, 0) translate(-50%, -50%)`;
    }

    if (trailRef.current) {
      trailRef.current.style.transform = `translate3d(${trailPos.current.x}px, ${trailPos.current.y}px, 0) translate(-50%, -50%)`;
    }
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let hideTimeout: ReturnType<typeof setTimeout> | null = null;

    const updateTargetPosition = () => {
      const rect = wrapper.getBoundingClientRect();

      targetPos.current = {
        x: lastMousePos.current.x - rect.left,
        y: lastMousePos.current.y - rect.top,
      };
    };

    const animate = () => {
      followerPos.current = {
        x: followerPos.current.x + (targetPos.current.x - followerPos.current.x) * 0.2,
        y: followerPos.current.y + (targetPos.current.y - followerPos.current.y) * 0.2,
      };

      trailPos.current = {
        x: trailPos.current.x + (followerPos.current.x - trailPos.current.x) * 0.12,
        y: trailPos.current.y + (followerPos.current.y - trailPos.current.y) * 0.12,
      };

      syncElements();
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (animationFrameRef.current !== null) return;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const stopAnimation = () => {
      if (animationFrameRef.current === null) return;
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    };

    const handleMouseMove = (e: MouseEvent) => {
      lastMousePos.current = { x: e.clientX, y: e.clientY };
      updateTargetPosition();
    };

    const handleMouseEnter = (e: MouseEvent) => {
      if (hideTimeout) clearTimeout(hideTimeout);
      lastMousePos.current = { x: e.clientX, y: e.clientY };
      updateTargetPosition();
      followerPos.current = { ...targetPos.current };
      trailPos.current = { ...targetPos.current };
      syncElements();
      setIsVisible(true);
      startAnimation();
    };

    const handleMouseLeave = () => {
      hideTimeout = setTimeout(() => {
        setIsVisible(false);
        stopAnimation();
      }, 120);
    };

    const handleScroll = () => {
      if (!isVisibleRef.current) return;
      updateTargetPosition();
    };

    wrapper.addEventListener('mousemove', handleMouseMove);
    wrapper.addEventListener('mouseenter', handleMouseEnter);
    wrapper.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      wrapper.removeEventListener('mousemove', handleMouseMove);
      wrapper.removeEventListener('mouseenter', handleMouseEnter);
      wrapper.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      if (hideTimeout) clearTimeout(hideTimeout);
      stopAnimation();
    };
  }, []);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div
        ref={trailRef}
        className={clsx(styles.cursorTrail, {
          [styles.visible]: isVisible,
        })}
      />

      <div
        ref={followerRef}
        className={clsx(styles.cursorFollower, {
          [styles.visible]: isVisible,
        })}
      />

      {children}
    </div>
  );
};